/**
 * The lifeline's toolbar and its "How to read this" key (Scott, 2026-10-08: "what do the
 * 'sliders' do" ... "some hover over text would help with navigating and understanding how
 * the page is intended to be consumed"). Both are hook-free: what they draw is a function of
 * their props, and the page (EmployeeLifeline) owns the state.
 *
 *   - the line chips are on/off checkboxes, under a visible "Show lines:" label;
 *   - Compare to peers, when it can't be drawn, says why beside itself and stays focusable
 *     (aria-disabled, never `disabled`, so hover, focus and touch all reach its tooltip);
 *   - every control carries a <Tip> in plain words.
 */
import { Tip } from './Tip'
import { LINES, fmtDate, peerShortReason } from './lifelineModel'

// What each view does: the view tabs' tooltips.
export const VIEW_HELP = {
  map: 'The whole career as lines over time: job, pay, manager, location, status and onboarding. Each change is a station on its line.',
  story: 'The career as one line, oldest first, grouped by year: every change as a stop with a dot in each changed line’s colour, and each before-hire step and finding in order.',
  table: 'Every change as a row: the date, what happened (a dot per line that changed), each line from → to, and UKG\'s reason with its code. Before-hire steps included.',
  pay: 'Every paycheck as a stripe: what it earned above the line, where the money went below. With Compare to peers on, a tick marks the peer median rate for the same hours.',
  patterns: 'The year spiral: one bead per week, one ring per year, January at the top, so the same week of every year lines up.',
}
export const PEERS_HELP = 'Draws what people in the same job and work location, paid the same way, are paid (hourly rates for hourly people, annual salaries for salaried people): a shaded band from their 25th to 75th percentile and a dashed line at their median, behind the Pay line and on the pay strata.'
export const ATTN_HELP = 'An open finding that names this person. Click to see the evidence.'
export const ASOF_HELP = 'The day this record was read. Open segments run to here; it is the map\'s Today line.'

export const chipHelp = (l) => `Show or hide the ${l.label} line: ${l.help}.`
export const peersUnavailableHelp = (reason) => `Compare to peers is unavailable for this person. ${reason || 'Peer comparison is not available.'}`

/**
 * The toolbar. `tabs` = [[key, label]]; `hidden` = the Set of lines turned off; `peers` = the
 * lifeline's peers section ({ available } or { available: false, reason }).
 */
export function LifelineToolbar({ tabs, view, onView, phone, showChips, hidden, onToggleLine, peers, peersOn, onPeers,
                                  howTo, onHowTo, howToRef = null, asOf, chipsLabelId = 'll-lines-label' }) {
  const peersAvailable = !!peers?.available
  return (
    <div className="ll-toolbar">
      <div className="ll-seg" role="group" aria-label="View">
        {tabs.map(([k, label]) => (
          <Tip key={k} text={k === 'map' && phone ? VIEW_HELP.story : VIEW_HELP[k]}>
            <button type="button" aria-pressed={view === k} onClick={() => onView(k)}>{label}</button>
          </Tip>
        ))}
      </div>
      {showChips && (
        <div className="ll-chips-wrap">
          <span className="ll-chips-label" id={chipsLabelId}>Show lines:</span>
          <div className="ll-chips" role="group" aria-labelledby={chipsLabelId}>
            {LINES.map(l => (
              <Tip key={l.key} text={chipHelp(l)}>
                <button type="button" className="ll-chip" aria-pressed={!hidden.has(l.key)} onClick={() => onToggleLine(l.key)}
                        style={{ '--ll-c': `var(--ll-${l.key})` }}>
                  <span className="ll-chip-box" aria-hidden="true">
                    <svg viewBox="0 0 12 12" width="12" height="12"><path d="M2.5 6.2 L5 8.6 L9.6 3.6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" /></svg>
                  </span>
                  <span className="ll-chip-text">{l.label}</span>
                </button>
              </Tip>
            ))}
          </div>
        </div>
      )}
      <span className="ll-spacer" />
      <button type="button" ref={howToRef} className="ll-link ll-howto-link" aria-expanded={!!howTo}
              aria-controls={howTo ? 'll-howto' : undefined} onClick={() => onHowTo(!howTo)}>How to read this</button>
      <div className="ll-peers-ctl">
        <Tip text={peersAvailable ? PEERS_HELP : peersUnavailableHelp(peers?.reason)}>
          <button type="button" className="ll-toggle" aria-pressed={!!peersOn} aria-disabled={peersAvailable ? undefined : 'true'}
                  onClick={() => { if (peersAvailable) onPeers(!peersOn) }}>
            <span className="ll-switch" aria-hidden="true" />Compare to peers
          </button>
        </Tip>
        {!peersAvailable && <span className="ll-peers-why">{peerShortReason(peers?.reason)}</span>}
      </div>
      <Tip text={ASOF_HELP}>
        <span className="ll-mono ll-asof" tabIndex={0}>As of {fmtDate(asOf)}</span>
      </Tip>
    </div>
  )
}

function Swatch({ k }) {
  return <i className="ll-key-swatch" style={{ background: `var(--ll-${k})` }} aria-hidden="true" />
}

/** "How to read this": the map's key on one screen. The page focuses its heading on open
 *  and closes it on Escape; the close button calls `onClose`. */
export function HowToKey({ onClose, phone, headingRef = null }) {
  return (
    <section id="ll-howto" className="ll-card ll-howto" role="region" aria-labelledby="ll-howto-h">
      <div className="ll-howto-top">
        <h2 id="ll-howto-h" tabIndex={-1} ref={headingRef}>How to read this</h2>
        <button type="button" className="ll-howto-close" onClick={onClose} aria-label="Close How to read this">✕</button>
      </div>
      <div className="ll-howto-grid">
        <div>
          <div className="ll-eyebrow">The lines</div>
          <ul className="ll-howto-list">
            {LINES.map(l => <li key={l.key}><Swatch k={l.key} /><span><b>{l.label}</b> — {l.help}.</span></li>)}
          </ul>
          {phone && <p className="ll-note">On a phone the map is the Story: one vertical line, oldest first.</p>}
        </div>
        <div>
          <div className="ll-eyebrow">In the Story and the Table</div>
          <ul className="ll-howto-list">
            <li><span className="ll-key-story" aria-hidden="true"><span className="ll-key-rail" /><span className="ll-key-stop" /></span>
              <span><b>Story</b> — the career as one vertical line, oldest first, grouped by year. Each stop is a change; its title is UKG's reason, with the reason code beside it.</span></li>
            <li><span className="ll-dots" aria-hidden="true"><span className="ll-dot" style={{ '--ll-c': 'var(--ll-job)' }} /><span className="ll-dot" style={{ '--ll-c': 'var(--ll-pay)' }} /></span>
              <span><b>Line dots</b> — one dot per line that changed, in the line's colour (here: job and pay).</span></li>
            <li><svg width="16" height="16" viewBox="0 0 14 14" aria-hidden="true"><path d="M7 1.5 L13 12.5 H1 Z" fill="var(--ll-high)" /></svg>
              <span><b>Finding marker</b> — in the Table, on a row whose date a finding names (the map's marker); click it to see the evidence.</span></li>
            <li><span><b>Pay</b> — a salaried person's pay reads as an annual salary, an hourly person's as an hourly rate.</span></li>
          </ul>
        </div>
        <div>
          <div className="ll-eyebrow">On the map</div>
          <ul className="ll-howto-list">
            <li><svg width="22" height="18" viewBox="0 0 22 18" aria-hidden="true"><line x1="0" y1="9" x2="22" y2="9" stroke="var(--ll-job)" strokeWidth="4" /><circle cx="11" cy="9" r="5" fill="#fff" stroke="var(--ll-job)" strokeWidth="3" /></svg>
              <span><b>Station</b> — a change on that date, on the line that changed. Click one to see what changed.</span></li>
            <li><svg width="22" height="26" viewBox="0 0 22 26" aria-hidden="true"><line x1="0" y1="7" x2="22" y2="7" stroke="var(--ll-job)" strokeWidth="4" /><line x1="0" y1="19" x2="22" y2="19" stroke="var(--ll-pay)" strokeWidth="4" /><rect x="5" y="1.5" width="12" height="23" rx="6" fill="#fff" stroke="var(--ll-ink)" strokeWidth="2.5" /></svg>
              <span><b>Interchange</b> — several lines changed in one transaction on that date.</span></li>
            <li><i className="ll-key-swatch ll-key-swatch--zone" aria-hidden="true" />
              <span><b>Before hire</b> — the shaded zone at the left: the hiring journey before day one, magnified (not to scale).</span></li>
            <li><svg width="22" height="18" viewBox="0 0 22 18" aria-hidden="true"><path d="M11 2 L18 15 H4 Z" fill="var(--ll-high)" /></svg>
              <span><b>Finding</b> — a marker on the top rail (triangle: high, diamond: medium or low) with a ring on the check it points at.</span></li>
            <li><i className="ll-key-swatch ll-key-swatch--peer" aria-hidden="true" />
              <span><b>Peer band</b> — with Compare to peers on: the 25th to 75th percentile of pay in the same job and location; the median is dashed.</span></li>
            <li><svg width="22" height="18" viewBox="0 0 22 18" aria-hidden="true"><line x1="11" y1="1" x2="11" y2="17" stroke="var(--ll-ink)" strokeWidth="1.5" strokeDasharray="4 3" /></svg>
              <span><b>Today</b> — the dashed line at the right: the day this record was read.</span></li>
          </ul>
        </div>
        <div>
          <div className="ll-eyebrow">The controls</div>
          <ul className="ll-howto-list">
            <li><span><b>Show lines</b> — a ticked chip shows its line on the map; click to hide it (dashed and struck through), click again to bring it back.</span></li>
            <li><span><b>Compare to peers</b> — draws the peer band. Greyed out, with the reason beside it, when it can't be shown (too few peers paid the same way, or no current pay rate).</span></li>
            <li><span><b>What needs attention</b> — open findings that name this person; click one to see its evidence.</span></li>
            <li><span>Hover, focus or tap anything for a short explanation.</span></li>
          </ul>
        </div>
      </div>
    </section>
  )
}
